import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

import ProgressiveImage from '../../../components/ui/ProgressiveImage';

const formatPrice = (price) => {
  if (!price) return 'Contact for price';
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 0 })?.format(price);
};

const VERDICT_CONFIG = {
  fair: { label: 'Fair', color: '#22C55E', bg: 'rgba(34, 197, 94, 0.15)' },
  risky: { label: 'Risky', color: '#F59E0B', bg: 'rgba(245, 158, 11, 0.15)' },
  aggressive: { label: 'Aggressive', color: '#EF4444', bg: 'rgba(239, 68, 68, 0.15)' },
  overpriced: { label: 'Overpriced', color: '#EF4444', bg: 'rgba(239, 68, 68, 0.15)' },
  underpriced: { label: 'Underpriced', color: '#22C55E', bg: 'rgba(34, 197, 94, 0.15)' },
};

const MobileComparisonCards = ({ properties, onRemove }) => {
  const navigate = useNavigate();
  const [activeIdx, setActiveIdx] = useState(0);
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (key) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev?.[key] }));
  };

  const prop = properties?.[activeIdx];
  const p = prop?.properties;
  const ai = prop?.aiAnalysis;
  const verdictCfg = ai?.verdict ? VERDICT_CONFIG?.[ai?.verdict?.toLowerCase()] : null;

  return (
    <div>
      {/* Property selector tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2 mb-4 scrollbar-hide">
        {properties?.map((property, idx) => (
          <button
            key={property?.id || idx}
            onClick={() => setActiveIdx(idx)}
            className="flex-shrink-0 px-4 py-2 rounded-xl text-sm font-medium transition-all"
            style={{
              background: activeIdx === idx ? 'rgba(0, 212, 255, 0.2)' : 'rgba(0, 212, 255, 0.05)',
              color: activeIdx === idx ? '#00D4FF' : '#6B9EC4',
              border: activeIdx === idx ? '1px solid rgba(0, 212, 255, 0.4)' : '1px solid rgba(0, 212, 255, 0.1)'
            }}
          >
            Property {idx + 1}
          </button>
        ))}
      </div>
      {/* Active property card */}
      <div
        className="rounded-2xl overflow-hidden"
        style={{ border: '1px solid rgba(0, 212, 255, 0.15)' }}
      >
        {/* Image */}
        <div className="relative h-48">
          <ProgressiveImage
            src={prop?.media?.[0]?.enhanced_url || prop?.media?.[0]?.original_url || '/assets/images/no_image.png'}
            alt={`Property at ${p?.address}`}
            className="w-full h-full"
          />
          <button
            onClick={() => onRemove(prop?.id)}
            className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center"
            style={{ background: 'rgba(255, 107, 107, 0.8)', color: 'white' }}
          >
            <Icon name="X" size={14} color="currentColor" />
          </button>
          <div
            className="absolute bottom-3 left-3 px-3 py-1 rounded-full text-xs font-semibold"
            style={{ background: 'rgba(10, 22, 40, 0.85)', color: '#00D4FF', border: '1px solid rgba(0, 212, 255, 0.3)' }}
          >
            {activeIdx + 1} of {properties?.length}
          </div>
        </div>

        {/* Details */}
        <div className="p-4" style={{ background: '#0D1B2A' }}>
          <h3 className="text-lg font-bold text-white mb-0.5">{p?.address}</h3>
          <p className="text-sm mb-3" style={{ color: '#6B9EC4' }}>{p?.suburb}{p?.state ? ', ' + p?.state : ''}</p>

          <p className="text-2xl font-bold mb-4" style={{ color: '#00D4FF' }}>{formatPrice(prop?.price_or_rent)}</p>

          {/* Quick stats */}
          <div className="grid grid-cols-4 gap-2 mb-4">
            {[{icon:'Bed',val:p?.beds,label:'Beds'},{icon:'Bath',val:p?.baths,label:'Baths'},{icon:'Car',val:p?.parking,label:'Parking'},{icon:'Home',val:p?.type,label:'Type'}]?.map(item => (
              <div
                key={item?.label}
                className="text-center p-2 rounded-xl"
                style={{ background: 'rgba(0, 212, 255, 0.05)', border: '1px solid rgba(0, 212, 255, 0.1)' }}
              >
                <Icon name={item?.icon} size={14} color="#6B9EC4" className="mx-auto mb-1" />
                <p className="text-sm font-bold text-white">{item?.val || '—'}</p>
                <p className="text-xs" style={{ color: '#4A6580' }}>{item?.label}</p>
              </div>
            ))}
          </div>

          {/* AI Verdict */}
          {verdictCfg && (
            <div
              className="flex items-center gap-2 px-3 py-2 rounded-xl mb-3"
              style={{ background: verdictCfg?.bg, border: `1px solid ${verdictCfg?.color}40` }}
            >
              <Icon name="Shield" size={16} color={verdictCfg?.color} />
              <span className="text-sm font-semibold" style={{ color: verdictCfg?.color }}>AI Verdict: {verdictCfg?.label}</span>
            </div>
          )}

          {/* Expandable sections */}
          {ai?.strengths?.length > 0 && (
            <div className="mb-2">
              <button
                onClick={() => toggleSection('strengths')}
                className="w-full flex items-center justify-between px-3 py-2 rounded-xl text-sm font-medium"
                style={{ background: 'rgba(34, 197, 94, 0.08)', color: '#22C55E' }}
              >
                <span className="flex items-center gap-2"><Icon name="ThumbsUp" size={14} color="currentColor" /> Strengths</span>
                <Icon name={expandedSections?.strengths ? 'ChevronUp' : 'ChevronDown'} size={14} color="currentColor" />
              </button>
              {expandedSections?.strengths && (
                <ul className="mt-2 space-y-1 px-3">
                  {ai?.strengths?.map((s, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs" style={{ color: '#8BA5C0' }}>
                      <Icon name="CheckCircle" size={12} color="#22C55E" className="mt-0.5 flex-shrink-0" />
                      {s}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {ai?.risks?.length > 0 && (
            <div className="mb-3">
              <button
                onClick={() => toggleSection('risks')}
                className="w-full flex items-center justify-between px-3 py-2 rounded-xl text-sm font-medium"
                style={{ background: 'rgba(245, 158, 11, 0.08)', color: '#F59E0B' }}
              >
                <span className="flex items-center gap-2"><Icon name="AlertTriangle" size={14} color="currentColor" /> Risks</span>
                <Icon name={expandedSections?.risks ? 'ChevronUp' : 'ChevronDown'} size={14} color="currentColor" />
              </button>
              {expandedSections?.risks && (
                <ul className="mt-2 space-y-1 px-3">
                  {ai?.risks?.map((r, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs" style={{ color: '#8BA5C0' }}>
                      <Icon name="AlertCircle" size={12} color="#F59E0B" className="mt-0.5 flex-shrink-0" />
                      {r}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <button
            onClick={() => navigate(`/listing/${prop?.id}`)}
            className="w-full py-2.5 rounded-xl text-sm font-semibold transition-all"
            style={{ background: 'rgba(0, 212, 255, 0.15)', color: '#00D4FF', border: '1px solid rgba(0, 212, 255, 0.3)' }}
          >
            View Full Analysis
          </button>
        </div>
      </div>
    </div>
  );
};

export default MobileComparisonCards;
